import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import Spinner from "../common/spinner";
import { ToastContainer, toast } from "react-toastify";
import "../completedOrders/completedOrders.scss";
import * as service from "../completedOrders/completedOrdersServices";
import DatePicker from "react-datepicker";
import Moment from "moment";
import { Column, Line, Area } from "@ant-design/plots";
import {
  timestampToDateYearAndTime,
  timestampToDateYearFormat,
  log,
} from "../../helper/helper";
import ridingImage from "../../assets/riding.png";
import walkingImage from "../../assets/walking.png";
import cyclingImage from "../../assets/cycling.png";
import drivingImage from "../../assets/driving.png";
import { Col, Divider, Row, Segmented } from "antd";

function fillMissingDatesGeneric(data, dateKey, valueKeys, groupKey) {
  if (!data.length) return [];

  const parseDate = (d) => new Date(d.split("/").reverse().join("-"));
  const formatDate = (d) =>
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  const sorted = [...data].sort(
    (a, b) => parseDate(a[dateKey]) - parseDate(b[dateKey])
  );

  const minDate = parseDate(sorted[0][dateKey]);
  const maxDate = parseDate(sorted[sorted.length - 1][dateKey]);

  // collect all groups (e.g. ["Rider", "Customer"])
  const groups = [...new Set(sorted.map((d) => d[groupKey]))];

  // make date range
  const dateRange = [];
  for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + 1)) {
    dateRange.push(formatDate(new Date(d)));
  }

  const filled = [];
  for (const date of dateRange) {
    for (const group of groups) {
      const existing = sorted.find(
        (d) => d[dateKey] === date && d[groupKey] === group
      );
      if (existing) {
        filled.push(existing);
      } else {
        const empty = { [dateKey]: date, [groupKey]: group };
        valueKeys.forEach((k) => (empty[k] = 0));
        filled.push(empty);
      }
    }
  }

  return filled;
}

function DailyActivity(props) {
  const [transports, setTransports] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [cursorTransportId, setCursorTransportId] = useState("");
  const [isHidden, setBtnHidden] = useState(false);
  const [startData, setStartDate] = useState("");
  const [endData, setEndDate] = useState("");
  const [totalPaidBearers, setTotalPaidBearers] = useState(0);
  const [totalPaidCustomers, setTotalPaidCustomers] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalFinalPaidRider, setTotalFinalPaidRider] = useState();
  const [totalOriginalPayableRider, setTotalOriginalPayableRider] = useState();
  const [totalTransportOriginalPrice, setTotalTransportOriginalPrice] =
    useState();
  const [totalTransportReceivablePrice, setTotalTransportReceivablePrice] =
    useState();
  const [paidCustomerRiderchartData, setPaidCustomerRiderChartData] = useState(
    []
  );
  const [totalTransportchartData, setTotalTransportChartData] = useState([]);
  const [profitLosschartData, setProfitLossChartData] = useState([]);
  const [segmentChart, setSegmentChart] = useState("Paid & Received");
  const maxdate = new Date();

  useEffect(() => {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      if (!user) {
        return props.history.replace("/login");
      }
    });
    let bearersTotal = 0;
    let customersTotal = 0;

    const filtered =
      selectedIds.length > 0
        ? transports.filter((t) => selectedIds.includes(t.transport_id))
        : transports;

    bearersTotal = filtered.reduce(
      (sum, t) => sum + (t.final_paid_to_rider || 0),
      0
    );

    customersTotal = filtered.reduce(
      (sum, t) => sum + (t.transport_receivable_price || 0),
      0
    );

    setTotalPaidBearers(bearersTotal);
    setTotalPaidCustomers(customersTotal);
  }, [selectedIds, transports]);

  const handleTransportDate = (time) => {
    return timestampToDateYearAndTime(time._seconds, time._nanoseconds);
  };

  const renderSpinner = () => {
    if (isLoading) {
      return <Spinner />;
    }
  };

  const handleSelectedRow = (transport) => {
    setSelectedIds((prev) =>
      prev.includes(transport.transport_id)
        ? prev.filter((id) => id !== transport.transport_id)
        : [...prev, transport.transport_id]
    );
  };

  const adminGetReportForCompleteTransport = async (newCall) => {
    if (startData === "") {
      return toast.error("Start date couldn't be empty");
    }
    if (endData === "") {
      return toast.error("End date couldn't be empty");
    }

    const paging = newCall ? "" : cursorTransportId;
    setLoading(true);
    try {
      const result = await service.adminGetReportForCompleteTransport(
        Moment(startData).format("YYYY-MM-DD"),
        Moment(endData).format("YYYY-MM-DD"),
        paging
      );
      setLoading(false);
      log(result);
      if (result) {
        setLoading(false);
        switch (result.data.code) {
          case 200:
            let updatedList = [];

            if (newCall) {
              adminGetFinancialReportForTransports();
              updatedList = result.data.transportList;
            } else {
              updatedList = [...transports, ...result.data.transportList];
            }

            setTransports(updatedList);

            if (result.data.cursor_transport_id) {
              setCursorTransportId(result.data.cursor_transport_id);
              setBtnHidden(false);
            } else {
              setCursorTransportId("");
              setBtnHidden(true);
            }

            const groupedData = {};
            const dailyCount = {};
            const dailyTotals = {};

            updatedList.forEach((item) => {
              const date = timestampToDateYearFormat(
                item.time._seconds,
                item.time._nanoseconds
              );

              if (!groupedData[date]) {
                groupedData[date] = { rider: 0, customer: 0 };
              }

              groupedData[date].rider += item.final_paid_to_rider || 0;
              groupedData[date].customer +=
                item.transport_receivable_price || 0;
              dailyCount[date] = (dailyCount[date] || 0) + 1;
              if (!dailyTotals[date]) {
                dailyTotals[date] = { receivable: 0, paidToRider: 0 };
              }
              dailyTotals[date].receivable +=
                item.transport_receivable_price || 0;
              dailyTotals[date].paidToRider += item.final_paid_to_rider || 0;
            });

            const chartData = Object.entries(groupedData)
              .sort(
                ([a], [b]) =>
                  new Date(a.split("/").reverse().join("-")) -
                  new Date(b.split("/").reverse().join("-"))
              )
              .flatMap(([date, value]) => [
                { name: "Bearers", Date: date, Price: +value.rider.toFixed(2) },
                {
                  name: "Customers",
                  Date: date,
                  Price: +value.customer.toFixed(2),
                },
              ]);

            const filledPaidCustomerRiderData = fillMissingDatesGeneric(
              chartData,
              "Date",
              ["Price"],
              "name"
            );
            setPaidCustomerRiderChartData(filledPaidCustomerRiderData);

            const totalTransportData = Object.entries(dailyCount)
              .sort(
                ([a], [b]) =>
                  new Date(a.split("/").reverse().join("-")) -
                  new Date(b.split("/").reverse().join("-"))
              )
              .map(([date, count]) => ({ day: date, count: count }));

            const filledTotalTransportData = fillMissingDatesGeneric(
              totalTransportData,
              "day",
              ["count"]
            );
            setTotalTransportChartData(filledTotalTransportData);

            const profitLossData = Object.entries(dailyTotals)
              .sort(
                ([a], [b]) =>
                  new Date(a.split("/").reverse().join("-")) -
                  new Date(b.split("/").reverse().join("-"))
              )
              .map(([date, values]) => {
                const { receivable, paidToRider } = values;
                const ratio =
                  receivable === 0
                    ? 0
                    : ((receivable - paidToRider) / paidToRider) * 100;
                return { x: date, y: parseFloat(ratio.toFixed(1)) };
              });

            const filledData = fillMissingDatesGeneric(profitLossData, "x", [
              "y",
            ]);
            function insertZeroCrossPoints(data) {
              const result = [];

              for (let i = 0; i < data.length; i++) {
                const curr = data[i];
                if (!curr || typeof curr.y !== "number" || !curr.x) continue; // skip invalid

                const type = curr.y >= 0 ? "pos" : "neg";
                const oppositeType = type === "pos" ? "neg" : "pos";

                // Always push the real point
                result.push({ x: curr.x, y: curr.y, type });

                // Always push the zero point with opposite type
                result.push({ x: curr.x, y: 0, type: oppositeType });
              }

              return result;
            }

            const finalProfitLossData = insertZeroCrossPoints(filledData);
            setProfitLossChartData(finalProfitLossData);
            return;

          case 401:
            toast.error(result.data.message);
            return props.history.replace("/login");
          case 500:
            return toast.error(result.data.message);
          default:
            return toast.error(result.data.message);
        }
      }
    } catch (error) {
      log(error);
      toast.error(error.message);
    }
    setLoading(false);
  };

  const adminGetFinancialReportForTransports = async () => {
    if (startData === "") {
      return toast.error("Start date couldn't be empty");
    }
    if (endData === "") {
      return toast.error("End date couldn't be empty");
    }
    setLoading(true);
    try {
      const result = await service.adminGetFinancialReportForTransports(
        Moment(startData).format("YYYY-MM-DD"),
        Moment(endData).format("YYYY-MM-DD")
      );
      setLoading(false);
      log(result);
      if (result) {
        setLoading(false);
        switch (result.data.code) {
          case 200:
            setTotalFinalPaidRider(result.data.total_final_paid_to_rider);
            setTotalOriginalPayableRider(
              result.data.total_original_payable_to_rider
            );
            setTotalTransportOriginalPrice(
              result.data.total_transport_original_price
            );
            setTotalTransportReceivablePrice(
              result.data.total_transport_receivable_price
            );
            return;
          case 401:
            toast.error(result.data.message);
            return props.history.replace("/login");
          case 500:
            return toast.error(result.data.message);
          default:
            return toast.error(result.data.message);
        }
      }
    } catch (error) {
      log(error);
      toast.error(error.message);
    }
    setLoading(false);
  };

  const handleBearerType = (bearer) => {
    switch (bearer) {
      case "driving":
        return (
          <div className="bearer">
            <img src={drivingImage} alt="rider" />
          </div>
        );
      case "riding":
        return (
          <div className="bearer">
            <img src={ridingImage} alt="rider" />
          </div>
        );
      case "cycling":
        return (
          <div className="bearer">
            <img src={cyclingImage} alt="cycle" />
          </div>
        );
      case "walking":
        return (
          <div className="bearer">
            <img src={walkingImage} alt="walk" />
          </div>
        );

      default:
        break;
    }
  };

  const paidToCustomerAndRiderconfig = {
    data: paidCustomerRiderchartData,
    xField: "Date",
    yField: "Price",
    colorField: "name",
    scale: {
      color: {
        domain: ["Bearers", "Customers"],
        range: ["#f00", "#71cf9a"],
      },
    },
    group: true,
    height: 300,
    style: {
      inset: 5,
    },
  };

  const totalOrderconfig = {
    data: totalTransportchartData,
    xField: "day",
    yField: "count",
    height: 200,
    point: {
      shapeField: "square",
      sizeField: 4,
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    style: {
      lineWidth: 2,
    },
  };

  const color = (d) => (d[0].type === "pos" ? "#409ef8" : "red");
  const colorCircle = (d) => (d.type === "pos" ? "#409ef8" : "red");
  const profitLossconfig = {
    data: profitLosschartData,
    height: 300,
    xField: "x",
    yField: "y",
    seriesField: "type",
    shapeField: "smooth",
    color,
    line: {
      style: {
        stroke: color,
      },
    },
    point: {
      shape: "circle",
      size: 2,
      style: {
        fill: colorCircle,
        stroke: "#fff",
      },
    },
    style: {
      fill: color,
      fillOpacity: 0.6,
    },
  };

  return (
    <div className="show-all-list data-server-list completed-orders">
      <ToastContainer />
      {renderSpinner()}
      <div className="filter-section">
        <Row gutter={16}>
          <Col span="10">
            <div
              className="form-group"
              style={{ display: "flex", alignItems: "center" }}
            >
              <label
                htmlFor="dateTime"
                style={{ marginBottom: 20, marginRight: 5 }}
              >
                Start:
              </label>
              <DatePicker
                selected={startData}
                onChange={(date) => {
                  setCursorTransportId("");
                  setStartDate(date);
                }}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                maxDate={maxdate}
              />
            </div>
          </Col>
          <Col span="10">
            <div
              className="form-group"
              style={{ display: "flex", alignItems: "center" }}
            >
              <label
                htmlFor="dateTime"
                style={{ marginBottom: 20, marginRight: 5 }}
              >
                End:
              </label>
              <DatePicker
                selected={endData}
                onChange={(date) => {
                  setCursorTransportId("");
                  setEndDate(date);
                }}
                dateFormat="yyyy-MM-dd"
                className="form-control"
                maxDate={maxdate}
              />
            </div>
          </Col>
          <Col span="2" style={{ textAlign: "right" }}>
            <button
              className="search-view"
              onClick={() => {
                adminGetReportForCompleteTransport(true);
              }}
              style={{ margin: "10px 0 20px" }}
            >
              Search
            </button>
          </Col>
        </Row>
      </div>
      {transports.length > 0 && (
        <>
          <Divider orientation="left">Number of Orders / Day</Divider>
          <Line {...totalOrderconfig} />
          <Segmented
            style={{
              backgroundColor: "#f8d7da",
              marginTop: 50,
            }}
            options={["Paid & Received", "Profit | Loss"]}
            onChange={(value) => {
              setSegmentChart(value);
            }}
          />
          {segmentChart === "Paid & Received" ? (
            <>
              <Divider orientation="left">
                Total Paid by Customers & Paid to Bearers
              </Divider>
              <Column {...paidToCustomerAndRiderconfig} />
            </>
          ) : (
            <>
              <Divider orientation="left">Profit | Loss (%)</Divider>
              <Area {...profitLossconfig} />
            </>
          )}
          <div style={{ padding: "20px 0" }}>
            <Row gutter={16}>
              <Col span="12">
                <div className="reports-number blue">
                  <span>Original Price</span>
                  <span style={{ fontWeight: "bold", fontSize: 18 }}>
                    {totalTransportOriginalPrice &&
                      totalTransportOriginalPrice.toFixed(2)}
                  </span>
                </div>
              </Col>
              <Col span="12">
                <div className="reports-number green">
                  <span>Total Paid to Customers</span>
                  <span style={{ fontWeight: "bold", fontSize: 18 }}>
                    {totalTransportReceivablePrice &&
                      totalTransportReceivablePrice.toFixed(2)}
                  </span>
                </div>
              </Col>
              <Col span="12">
                <div className="reports-number orange">
                  <span>Original Payable to Bearers</span>
                  <span style={{ fontWeight: "bold", fontSize: 18 }}>
                    {totalFinalPaidRider && totalFinalPaidRider.toFixed(2)}
                  </span>
                </div>
              </Col>
              <Col span="12">
                <div className="reports-number pink">
                  <span>Final Paid to Bearers</span>
                  <span style={{ fontWeight: "bold", fontSize: 18 }}>
                    {totalOriginalPayableRider &&
                      totalOriginalPayableRider.toFixed(2)}
                  </span>
                </div>
              </Col>
            </Row>
            <Divider />
            <Row gutter={16}>
              <Col span="12">
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#f00",
                  }}
                >
                  Total Paid to Bearers: {totalPaidBearers.toFixed(2)}
                </div>
              </Col>
              <Col span="12">
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#71cf9a",
                  }}
                >
                  Total Paid to Customers: {totalPaidCustomers.toFixed(2)}
                </div>
              </Col>
            </Row>
          </div>
          <h6 style={{ margin: "20 0" }}>
            Found {transports.length}{" "}
            {transports.length > 1 ? "results" : "result"}
          </h6>
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Origin Address</th>
                <th>Customer Name</th>
                <th>Destination Address</th>
                <th>Bearer Name</th>
                <th>Assigned MoT</th>
                <th>Final Paid by Customer</th>
                <th>Final Paid to Rider</th>
                <th>(More Details)</th>
              </tr>
            </thead>
            <tbody>
              {transports.map((transport) => (
                <tr
                  key={transport.transport_id}
                  onClick={() => handleSelectedRow(transport)}
                  style={{
                    backgroundColor: selectedIds.includes(
                      transport.transport_id
                    )
                      ? "#dee2e6"
                      : "transparent",
                    cursor: "pointer",
                  }}
                >
                  <td>{handleTransportDate(transport.time)}</td>
                  <td>{transport.origin_address}</td>
                  <td>
                    {transport.customer_given_name
                      ? transport.customer_given_name
                      : "" + " " + transport.customer_last_name
                      ? transport.customer_last_name
                      : ""}
                  </td>
                  <td>{transport.destination_address}</td>
                  {/* <td>{transport.latest_status}</td> */}
                  <td>
                    {transport.rider_given_name +
                      " " +
                      transport.rider_last_name}
                  </td>
                  <td>{handleBearerType(transport.bearer_type)}</td>
                  <td>{"A$ " + transport.transport_receivable_price}</td>
                  <td>{"A$ " + transport.final_paid_to_rider}</td>
                  <td>
                    <button
                      onClick={() =>
                        props.history.push("/home/transportSingle", {
                          transportId: transport.transport_id,
                        })
                      }
                      className="single-view"
                    >
                      view
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="load-more">
            <button
              hidden={isHidden}
              onClick={() => adminGetReportForCompleteTransport(false)}
            >
              Load More
            </button>
          </div>
        </>
      )}
    </div>
  );
}
export default DailyActivity;
